<template>
  <div class="message-box dialog-mask" @click.self="onClickOutSide">
    <div class="dialog-box">
      <div class="section dialog-header">
        <h2>{{ $t("myPage.userDeleteDialog.title") }}</h2>
        <h3>
          <span>{{ $t("myPage.userDeleteDialog.description1") }}</span>
          <span class="color-danger">{{ $t("myPage.userDeleteDialog.description2") }}</span>
          <span>{{ $t("myPage.userDeleteDialog.description3") }}</span>
        </h3>
      </div>
      <div v-if="existsNolaPublishingUser || existsNolaNovelUser" class="section dialog-body">
        <div>
          {{ $t("myPage.userDeleteDialog.relatedServicesNotice") }}
        </div>
        <div class="related-services">
          <div v-if="existsNolaNovelUser" class="font-bold">{{ $t("myPage.userDeleteDialog.nolaNovel") }}</div>
          <div v-if="existsNolaPublishingUser" class="font-bold">{{ $t("myPage.userDeleteDialog.nolaPublishing") }}</div>
        </div>
      </div>
      <div class="section dialog-body">
        <div>{{ $t("myPage.userDeleteDialog.confirmNotice") }}</div>
        <div class="checkbox" @click="onClickCheckbox">
          <checkbox-icon class="form-input" :isChecked="confirmed" :fill="'#389bff'" />
          <span class="font-bold">{{ $t("myPage.userDeleteDialog.checkboxLabel") }}</span>
        </div>
        <div>
          <div class="color-danger">
            {{ $t("myPage.userDeleteDialog.contactNoticePrefix") }}
            <router-link to="/contact" @click.native="onClickContact">{{ $t("common.contact") }}</router-link>
            {{ $t("myPage.userDeleteDialog.contactNoticeSuffix") }}
          </div>
        </div>
      </div>
      <div class="section button-area">
        <button class="button font-bold" @click="onNegativeClick">{{ negative }}</button>
        <button class="button danger font-bold" :disabled="!confirmed" @click="onPositiveClick">{{ positive }}</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import CheckboxIcon from "@/components/atoms/CheckboxIcon.vue";

export default Vue.extend<Data, Methods, {}, Props>({
  components: {
    CheckboxIcon,
  },
  props: {
    existsNolaPublishingUser: {
      type: Boolean,
      default: false,
    },
    existsNolaNovelUser: {
      type: Boolean,
      default: false,
    },
    negative: {
      type: String,
      default() {
        return this.$t("common.cancel") as string;
      },
    },
    positive: {
      type: String,
      default() {
        return this.$t("common.yes") as string;
      },
    },
    negativeCallback: Function,
    positiveCallback: Function,
  },
  data() {
    return {
      confirmed: false,
    };
  },
  methods: {
    onNegativeClick() {
      if (this.negativeCallback) {
        this.negativeCallback();
      }

      this.$close(true);
    },
    onPositiveClick() {
      if (this.positiveCallback) {
        this.positiveCallback();
      }

      this.$close(true);
    },
    onClickOutSide() {
      this.$close(true);
    },
    onClickCheckbox() {
      this.confirmed = !this.confirmed;
    },
    onClickContact() {
      this.$close(true);
    },
  },
});

interface Props {
  existsNolaPublishingUser: boolean;
  existsNolaNovelUser: boolean;
  negative?: string;
  positive?: string;
  negativeCallback?: () => void;
  positiveCallback?: () => void;
}

interface Data {
  confirmed: boolean;
}

interface Methods {
  onNegativeClick: () => void;
  onPositiveClick: () => void;
  onClickOutSide: () => void;
  onClickCheckbox: () => void;
  onClickContact: () => void;
}

export type UserDeleteDialogProps = Props;
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.fade-enter-active {
  transition: opacity 0.3s;
}

.fade-leave-active {
  transition: opacity 0.1s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.dialog-mask {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: -ms-flexbox;
  display: flex;
  z-index: 9000;
  -ms-flex-align: center;
  align-items: center;
  -ms-flex-pack: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.dialog-box {
  max-width: 800px;
  margin: 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-background-normal);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);

  .section {
    border-bottom: 1px solid var(--color-border);
    box-sizing: border-box;
    padding: 20px;
  }

  .dialog-header {
    h2 {
      text-align: center;
      margin-top: 0;
    }

    h3 {
      margin: 0;
    }
  }

  .dialog-body {
    font-size: 1.1em;

    .related-services {
      box-sizing: border-box;
      padding: 10px 20px;
      background-color: var(--color-surface-muted);
      border-radius: var(--radius-sm);
      margin-top: 10px;
    }

    .checkbox {
      margin: 25px 5px;
      display: flex;
      align-items: center;

      .form-input {
        margin-right: 5px;
      }
    }
  }

  .button-area {
    border-bottom: none;
    display: flex;
    justify-content: center;

    .button {
      width: 100px;
      box-sizing: border-box;
      border-radius: var(--radius-xs);

      &.danger {
        &:disabled {
          background-color: var(--color-surface-muted);
          border: solid 1px var(--color-border);
        }
      }
    }
  }
}

.color-danger {
  color: var(--color-warning);
}

.font-bold {
  font-weight: var(--font-weight-bold);
}
</style>
